/* eslint-disable import/no-cycle */
import { getDefaultMiddleware } from '@reduxjs/toolkit';
import { applyMiddleware, combineReducers, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createMigrate, persistReducer, persistStore } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import thunk from 'redux-thunk';

import authReducer from './slices/authSlice';
import careAdvisorMyProfile from './slices/careAdvisorMyProfile';
import clientBranding from './slices/clientBrandingSlice';
import columnVisibility from './slices/columnVisibilitySlice';
import staffCaseCreation from './slices/staffCaseCreationSlice';
import conversationStatisticsReducer from './slices/conversationStatisticsSlice';
import currentPatientState from './slices/currentPatientState';
import dependents from './slices/dependentsSlice';
import isAdminNewDesign from './slices/isAdminNewDesignSlice';
import isFromHandOff from './slices/isFromHandOffSlice';
import linkedCase from './slices/linkedCaseSlice';
import location from './slices/location';
import messageDrafts from './slices/messageDraftsSlice';
import modal from './slices/modalSlice';
import myProfileSidebar from './slices/myProfileSidebarSlice';
import oneReport from './slices/oneReportSlice';
import organizationPrices from './slices/organizationPricingSlice';
import organization from './slices/organizationSlice';
import pageState from './slices/pageStateSlice';
import patientForm from './slices/patientFormSlice';
import patientMyProfile from './slices/patientMyProfileSlice';
import providerMyProfile from './slices/providerMyProfileSlice';
import selectedPatient from './slices/selectedPatientSlice';
import videoThumbnail from './slices/showVideoThumbnailSlice';
import sidebarState from './slices/sidebarStateSlice';
import smileScanCase from './slices/smileScanCaseSlice';
import tablePagination from './slices/tablePaginationSlice';
import videoCallCredentials from './slices/videoCallCredentialsSlice';

const rootReducer = combineReducers({
  auth: authReducer,
  staffCaseCreation,
  selectedPatient,
  pageState,
  patientForm,
  linkedCase,
  patientMyProfile,
  providerMyProfile,
  careAdvisorMyProfile,
  oneReport,
  organization,
  organizationPrices,
  clientBranding,
  location,
  modal,
  videoCallCredentials,
  dependents,
  currentPatientState,
  smileScanCase,
  isFromHandOff,
  videoThumbnail,
  sidebarState,
  myProfileSidebar,
  conversationStatistics: conversationStatisticsReducer,
  isAdminNewDesign,
  messageDrafts,
  columnVisibility,
  tablePagination,
});

const middleware = [
  ...getDefaultMiddleware({
    serializableCheck: false,
  }),
  thunk,
];

const persistMigrations = {
  1: (state: Record<string, unknown> | undefined) => {
    if (state == null || typeof state !== 'object') return Promise.resolve(state);
    const { collectorCaseCreation: _drop, ...rest } = state;
    return Promise.resolve(rest);
  },
  2: (state: Record<string, unknown> | undefined) => {
    if (state == null || typeof state !== 'object') return Promise.resolve(state);
    const { collectorCaseCreation: _c, staffCaseCreation: _s, ...rest } = state as Record<string, unknown> & {
      collectorCaseCreation?: unknown;
      staffCaseCreation?: unknown;
    };
    return Promise.resolve(rest);
  },
};

const persistConfig = {
  key: 'root',
  version: 2,
  storage,
  /** Ephemeral: if persisted, stuck `true` hides the staff-intake chrome on dashboard after refresh. */
  blacklist: ['sidebarState', 'staffCaseCreation'],
  migrate: createMigrate(persistMigrations, { debug: false }),
};

const preloadedState: any = {};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const composeEnhancers = composeWithDevTools({});

const store = createStore(persistedReducer, preloadedState, composeEnhancers(applyMiddleware(...middleware)));

export const persistor = persistStore(store);

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;

export default store;
